import { ApckPlaceholder } from '../common/placeholder';
import { ApckSnapshotsGallery } from '../goscript/goscript';
import AjaxUtils from '../../util/ajax_utils';
import { createUUIDv4 } from '../../util/helpers';
import { ApckNarrative, normaliseNarrativeFileType } from '../goscript/narrative';

export const ApckVulnAttachmentsGallery = {
    // Currently, we only output images,
    // but we plan to support other attachment types later.
    components: {
        'apck-snapshots-gallery': ApckSnapshotsGallery,
        'apck-placeholder': ApckPlaceholder,
    },
    props: {
        attachments: {
            type: Array,
            default: [],
        },
    },
    data() {
        return {
            snapshots: [],
            events: [],
            loading: false,
        };
    },
    computed: {
        totalSnapshotsCount() {
            return String(this.snapshots.length).padStart(2, '0');
        },
        snapshotsCount() {
            return String(this.events.length / 2 + 1).padStart(2, '0');
        },
    },
    mounted() {
        this.loadSnapshots();
    },
    watch: {
        attachments: {
            handler() {
                this.loadSnapshots();
            },
            immediate: false,
        },
    },
    methods: {
        loadSnapshots() {
            // We request a signed URL for each attachment
            // and convert it into the events format,
            // so it can be rendered using the GoScript gallery component
            // similar to how it's handled in the GoScript editor gallery.
            this.loading = true;
            this.snapshots = this.attachments.filter((item) => {
                return item.file_extension === 'png';
            });
            this.handleAttachment(this.snapshots, 0);
        },
        handleAttachment(attachments, index) {
            // Recurrent method to handle attachments one by one
            const cmp = this;
            if (index >= attachments.length) {
                cmp.loading = false;
                return;
            }
            const item = attachments[index];

            const url = `/vuln-control/attachment?attachment_id=${encodeURIComponent(item.attachment_id)}`;
            AjaxUtils.ajax_get({
                url,
                success: (data) => {
                    const downloadUrl = data['download_url'];
                    const snapshotEvent = {
                        timestamp: cmp.formatTimestamp(item.timestamp),
                        type: 'snapshot',
                        id: createUUIDv4(),
                        group: '',
                        data: downloadUrl,
                    };

                    const logEvent = {
                        timestamp: cmp.formatTimestamp(item.timestamp),
                        type: 'log_info',
                        id: createUUIDv4(),
                        data: item.label,
                    };

                    cmp.events.push(logEvent);
                    cmp.events.push(snapshotEvent);

                    cmp.handleAttachment(attachments, index + 1);
                },
                fail: () => {
                    cmp.$flash({
                        type: 'error',
                        message: 'Failed to download the attachment. Please try again.',
                    });
                    cmp.loading = false;
                },
            });
        },
        formatTimestamp(unixTimestamp) {
            return new Date(unixTimestamp * 1000).toISOString().slice(0, 19).replace('T', ' ');
        },
    },
    template: `
        <div v-if="snapshots.length > 0" class="goscript-bottom">
            <div v-if="loading" class="goscript-bottom__title goscript-bottom__title--numeric">Loading {{ snapshotsCount }} of {{ totalSnapshotsCount }} snapshots…</div>
            <div v-else class="goscript-bottom__title goscript-bottom__title--numeric">Snapshots ({{ totalSnapshotsCount }})</div>
            <apck-snapshots-gallery :events="events" />
        </div>
    `,
};

export const ApckVulnAttachments = {
    // Currently, we only output images,
    // but we plan to support other attachment types later.
    components: {
        'apck-vuln-attachments-gallery': ApckVulnAttachmentsGallery,
        'apck-narrative': ApckNarrative,
    },
    props: {
        attachments: {
            type: Array,
            default: [],
        },
        scanSettingsUrl: {
            type: String,
            default: null,
        },
        goScriptUrl: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            snapshots: [],
            events: [],
            loading: false,
            narrative: null,
        };
    },
    computed: {
        hasNarrative() {
            return this.narrative;
        },
        isGallery() {
            return this.attachments.some(item => item.file_extension === 'png');
        },
    },
    mounted() {
        this.loading = true;
        this.checkAttachments();
    },
    watch: {
        attachments: {
            handler() {
                this.checkAttachments();
            },
            immediate: false,
        },
    },
    methods: {
        checkAttachments() {
            const jsonAttachments = this.attachments.filter((item) => {
                const fileExtension = item.file_extension?.replace(/^\./, '');
                return fileExtension === 'json';
            });
            if (jsonAttachments.length > 0) {
                this.checkNarrative(jsonAttachments, 0);
            } else {
                this.loading = false;
            }
        },
        checkNarrative(attachments, index) {
            const cmp = this;
            if (index >= attachments.length) {
                cmp.loading = false;
                return;
            }

            const item = attachments[index];

            const url = `/vuln-control/attachment?attachment_id=${encodeURIComponent(item.attachment_id)}`;
            AjaxUtils.ajax_get({
                url,
                success: (data) => {
                    const downloadUrl = data['download_url'];
                    AjaxUtils.ajax_get({
                        dataType: 'json',
                        url: downloadUrl,
                        success: (attachmentData) => {
                            if (attachmentData && attachmentData.type === 'narrative') {
                                cmp.narrative = attachmentData;
                                cmp.loading = false;
                            } else {
                                cmp.checkNarrative(attachments, index + 1);
                            }
                        },
                    });
                },
            });
        },
    },
    template: `
        <div>
            <strong v-if="loading">
                <span class="fa fa-spin fa-spinner"></span> Loading attachments, please wait
            </strong>
            <div v-if="hasNarrative && !loading">
                <apck-narrative
                    :narrative="narrative"
                    :scan-settings-url="scanSettingsUrl"
                    :go-script-url="goScriptUrl"
                    attachment-url="/vuln-control/attachment"
                >
                </apck-narrative>
            </div>
            <div v-if="!loading && isGallery && !hasNarrative">
                <apck-vuln-attachments-gallery :attachments="attachments"></apck-vuln-attachments-gallery>
            </div>
        </div>
    `,
};
